import {Component, OnDestroy, OnInit} from '@angular/core';
import {Invoice} from '../../model/invoice';
import {InvoicePeerService} from '../../service/invoice-peer.service';
import {InvoiceAttachmentsResource, InvoiceAttachmentTypesResource} from '../../model/model-resource';
import {InvoiceAttachment} from '../../model/invoice-attachment';
import {ObjectQueryFilterOperator, ObjectQueryParameters, ObjectQuerySortDirection} from '../../model/object-query-parameters';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {InvoiceAttachmentType} from '../../model/invoice-attachment-type';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {forkJoin, Observable, Subject} from 'rxjs';
import {saveAs} from 'file-saver';
import {InvoiceAttachmentTypesMap, InvoiceAttachmentTypesMapCache} from '../../model/model-map-cache';
import { RealtimeNotificationsService } from '../../service/realtime-notifications.service';

@Component({
  selector: 'app-invoice-attachments-widget',
  templateUrl: './invoice-attachments-widget.component.html',
  styleUrls: ['./invoice-attachments-widget.component.scss']
})
export class InvoiceAttachmentsWidgetComponent implements OnInit, OnDestroy {

  invoice$: Observable<Invoice>;
  invoiceAttachments$: Observable<InvoiceAttachment[]>;
  invoiceAttachmentTypesMap$: Observable<InvoiceAttachmentTypesMap>;

  invoice: Invoice;
  selectedItem: InvoiceAttachment;
  uploadFiles: File[];

  hiddenChoices = [{
    value: true, label: 'Yes'
  }, {
    value: false, label: 'No'
  }];

  form: UntypedFormGroup;
  locked = false;
  edited = false;
  added = false;
  saving = false;

  private readonly ngDestroy$ = new Subject();

  constructor(
    private invoicePeerService: InvoicePeerService,
    private invoiceAttachmentsResource: InvoiceAttachmentsResource,
    private invoiceAttachmentTypesResource: InvoiceAttachmentTypesResource,
    private invoiceAttachmentTypesMapCache: InvoiceAttachmentTypesMapCache,
    private realtime: RealtimeNotificationsService,
    private builder: UntypedFormBuilder) {
    this.form = this.builder.group({
      invoiceAttachmentTypeId: [null],
      description: [null],
      isHidden: [null]
    });
  }

  add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.uploadFiles = [];
  }

  edit(): void {
    this.invoicePeerService.lock();
    this.form.patchValue(this.selectedItem);
    this.edited = true;
  }

  revert(): void {
    this.invoicePeerService.unlock();
    this.edited = false;
    this.added = false;
    this.uploadFiles = [];
  }

  save(): void {
    this.saving = true;
    const invoiceAttachment = this.selectedItem.clone();
    Object.assign(invoiceAttachment, this.form.value);
    this.invoiceAttachmentsResource.save(invoiceAttachment).subscribe(() => {
      this.invoicePeerService.unlock();
      this.saving = false;
      this.edited = false;
      this.selectedItem = null;
      this.invoiceAttachments$ = this.getInvoiceAttachments(this.invoice.invoiceId);
    });
  }

  download(invoiceAttachment: InvoiceAttachment): void {
    this.invoiceAttachmentsResource.get(invoiceAttachment.invoiceAttachmentId).subscribe((attachment: InvoiceAttachment) => {
      const byteCharacters = atob(attachment.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {type: attachment.contentType});
      saveAs(blob, attachment.originalFileName);
    });
  }

  upload(): void {

    this.saving = true;

    const uploads = this.uploadFiles.map((file: File) => {
      return this.uploadFile(file);
    });

    forkJoin(...uploads).subscribe(() => {
      this.invoicePeerService.unlock();
      this.saving = false;
      this.added = false;
      this.selectedItem = null;
      this.invoiceAttachments$ = this.getInvoiceAttachments(this.invoice.invoiceId);
    });

  }

  uploadFile(file: File): Observable<InvoiceAttachment> {

    const convert = new Observable<string | ArrayBuffer>((obs) => {
      const reader = new FileReader();
      reader.onerror = err => obs.error(err);
      reader.onabort = err => obs.error(err);
      reader.onload = () => obs.next(reader.result);
      reader.onloadend = () => obs.complete();
      return reader.readAsBinaryString(file);
    });

    return convert.pipe(
      switchMap((content: string) => {
        const attachment = new InvoiceAttachment();
        attachment.invoiceId = this.invoice.invoiceId;
        attachment.originalFileName = file.name;
        attachment.content = btoa(content);
        return this.invoiceAttachmentsResource.save(attachment);
      })
    );
  }

  uploadFileAdded(file: File): void {
    this.uploadFiles.push(file);
  }

  removeUploadFile(file: File): void {
    this.uploadFiles = this.uploadFiles.filter(f => f !== file);
  }

  itemClicked(item: InvoiceAttachment) {
    this.selectedItem = (item !== this.selectedItem ? item : null);
  }

  ngOnDestroy() {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }

  ngOnInit() {

    this.invoiceAttachmentTypesMap$ = this.invoiceAttachmentTypesMapCache.map$;

    this.invoice$ = this.invoicePeerService
      .get()
      .pipe(
        takeUntil(this.ngDestroy$),
        tap(invoice => {
            this.invoice = invoice;
            this.invoiceAttachments$ = this.getInvoiceAttachments(invoice.invoiceId);
          }
        ));

    this.invoicePeerService.getState().pipe(takeUntil(this.ngDestroy$)).subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });

    // AI: Refresh attachment list when another user or backend process creates/updates an attachment.
    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((event) => {
        if (
          (event.event_type === 'invoice_attachment_created' ||
            event.event_type === 'invoice_attachment_updated') &&
          this.invoice &&
          event.invoice_id === this.invoice.invoiceId
        ) {
          this.invoiceAttachments$ = this.getInvoiceAttachments(this.invoice.invoiceId);
        }
      });
  }

  private getInvoiceAttachmentTypes(): Observable<InvoiceAttachmentType[]> {
    const params = new ObjectQueryParameters();
    params.addSort('description', ObjectQuerySortDirection.Ascending);
    return this.invoiceAttachmentTypesResource
      .query(params);
  }

  private getInvoiceAttachments(invoiceId: number): Observable<InvoiceAttachment[]> {
    const params = new ObjectQueryParameters();
    params.addFilter('invoiceId', ObjectQueryFilterOperator.Equals, invoiceId.toString());
    params.addFilter('isHidden', ObjectQueryFilterOperator.Equals, false.toString());
    params.addSort('created', ObjectQuerySortDirection.Descending);
    return this.invoiceAttachmentsResource
      .query(params);
  }
}
